import { useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { post } from '@/utilities/onwardClient';
import { SET_CARRIER_INTEGRATIONS_REQUIRED } from '@/constants/apiRoutes';
import { GET_ORG_CARRIER_RELATIONSHIP } from '@/components/CorporateCarrier/graphql';

// The onboarding view reads the same flag, so a change re-reads it too, when it's mounted.
const FLAG_QUERIES = ['GetOrgCarrierRelationship', 'GetOrgCarrierOnboarding'];
const refetchesFlag = (observableQuery) => FLAG_QUERIES.includes(observableQuery.queryName);

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

/**
 * The carrier's relationship to the org and its "No External Integrations Required" flag, and a setter that saves the
 * flag through setCarrierIntegrationsRequired.
 * @param {{ carrierId: string, organizationId: string, notify: (notification: object) => void }} args
 */
export const useIntegrationsFlag = ({ carrierId, organizationId, notify }) => {
    const apolloClient = useApolloClient();
    const [saving, setSaving] = useState(false);

    const { data, loading } = useQuery(GET_ORG_CARRIER_RELATIONSHIP, {
        variables: { organization_id: organizationId, carrier_id: carrierId },
        skip: !organizationId || !carrierId,
    });
    const status = data?.organization_carrier_statuses?.[0];

    const setNoIntegrationsRequired = useCallback(
        async (noIntegrationsRequired) => {
            setSaving(true);
            try {
                // organization_id is only read by the emulator; deployed environments take it from the token.
                await post(SET_CARRIER_INTEGRATIONS_REQUIRED, {
                    carrier_id: carrierId,
                    organization_id: organizationId,
                    integrations_required: !noIntegrationsRequired,
                });
                await apolloClient.refetchQueries({ include: 'active', onQueryUpdated: refetchesFlag });
                notify?.({
                    severity: 'success',
                    message: noIntegrationsRequired
                        ? 'Marked No External Integrations Required'
                        : 'Integrations required',
                });
            } catch (err) {
                Sentry.captureException(err);
                notify?.({ severity: 'error', message: errorMessage(err, 'Error saving the integrations setting') });
            } finally {
                setSaving(false);
            }
        },
        [apolloClient, carrierId, notify, organizationId]
    );

    return {
        relationship: status?.relationship,
        // An account with no organization_accounts row reads as integrations required.
        noIntegrationsRequired: status ? !status.integrations_required : false,
        loading,
        saving,
        setNoIntegrationsRequired,
    };
};
